import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatDateTime, formatIDR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  ArrowLeft, User as UserIcon, MapPin, Lock, Eye, EyeOff, Globe,
  ShieldCheck, Settings as SettingsIcon, LogOut, Star,
  CreditCard, Gift, Copy, Users as UsersIcon, TrendingUp,
} from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import StarRating from "@/components/StarRating";

export default function Profile() {
  const { user, login, logout } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    location: user?.location || "",
    default_catalog_privacy: user?.default_catalog_privacy || "public",
    hide_prices_from_search: !!user?.hide_prices_from_search,
    // Iter 25 — bank info
    bank_account_number: user?.bank_account_number || "",
    bank_name: user?.bank_name || "",
    bank_account_holder: user?.bank_account_holder || "",
  });
  const [saving, setSaving] = useState(false);
  // Iter 25 — referral state
  const [referral, setReferral] = useState(null);
  const [refCodeDraft, setRefCodeDraft] = useState("");
  const [savingRefCode, setSavingRefCode] = useState(false);
  const [leaderboardEnabled, setLeaderboardEnabled] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/referral/me").then((r) => setReferral(r.data)).catch(() => {});
    api.get("/fee-config").then((r) => setLeaderboardEnabled(r.data?.leaderboard_enabled !== false)).catch(() => {});
  }, [user]);

  if (!user) return <div className="text-center py-20" style={{color: "var(--app-muted)"}}>Memuat...</div>;

  // Iter 11+: every user has both roles. Show privacy settings whenever account
  // has jastiper role (which is everyone under unified accounts).
  const roles = user.roles || (user.role ? [user.role] : []);
  const hasJastiperRole = roles.includes("jastiper");
  const activeMode = user.active_mode || user.role || "buyer";
  const dirty =
    form.name !== (user.name || "") ||
    form.phone !== (user.phone || "") ||
    form.location !== (user.location || "") ||
    form.default_catalog_privacy !== (user.default_catalog_privacy || "public") ||
    form.hide_prices_from_search !== !!user.hide_prices_from_search ||
    form.bank_account_number !== (user.bank_account_number || "") ||
    form.bank_name !== (user.bank_name || "") ||
    form.bank_account_holder !== (user.bank_account_holder || "");

  const saveReferralCode = async () => {
    const code = refCodeDraft.trim().toLowerCase();
    if (!/^[a-z0-9_-]{3,32}$/.test(code)) {
      toast.error("Kode harus 3-32 karakter: a-z, 0-9, _ atau -");
      return;
    }
    setSavingRefCode(true);
    try {
      const r = await api.put("/auth/profile", { referral_code: code });
      const token = localStorage.getItem("jastip_token");
      login(token, r.data);
      const ref = await api.get("/referral/me");
      setReferral(ref.data);
      setRefCodeDraft("");
      toast.success("Kode referral tersimpan");
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal menyimpan kode");
    } finally {
      setSavingRefCode(false);
    }
  };

  const copyRefLink = () => {
    const link = `${window.location.origin}/auth?ref=${referral?.referral_code}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success("Link referral disalin"),
      () => toast.error("Gagal menyalin")
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/auth/profile", form);
      const token = localStorage.getItem("jastip_token");
      login(token, r.data);
      toast.success("Profil tersimpan");
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="profile-page">
      <Link to="/dashboard" className="text-sm flex items-center gap-2" style={{color: "var(--app-muted)"}} data-testid="profile-back-link">
        <ArrowLeft size={14} strokeWidth={1.5} /> Beranda
      </Link>

      <div>
        <div className="label-caps mb-2">Akun</div>
        <h1 className="font-display text-4xl tracking-tight">Profil Saya</h1>
        <div className="font-mono text-xs mt-2" style={{color: "var(--app-muted)"}}>
          {user.email} ·{" "}
          <span
            className="inline-block px-1.5 py-0.5 font-bold tracking-wider rounded text-white"
            style={{ backgroundColor: activeMode === "jastiper" ? "#059669" : "#002FA7" }}
            data-testid="profile-active-mode-pill"
          >
            {activeMode === "jastiper" ? "JASTIPER" : "PEMBELI"}
          </span>
          <span className="ml-1" style={{color: "var(--app-muted)"}}>(mode aktif)</span>
        </div>
      </div>

      {/* Avatar / Identity card */}
      <div className="surface p-6 flex items-center gap-4" data-testid="profile-identity">
        <div className="w-16 h-16 flex items-center justify-center font-display text-2xl text-white" style={{backgroundColor: "var(--app-brand)"}}>
          {user.name?.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="font-display text-2xl tracking-tight inline-flex items-center gap-2">
            {user.name}
            {user.verified_badge && <VerifiedBadge size={18} testid="profile-verified-badge" />}
          </div>
          <div className="font-mono text-xs mt-1" style={{color: "var(--app-muted)"}}>
            Bergabung sejak {new Date(user.created_at || Date.now()).toLocaleDateString("id-ID", { month: "short", year: "numeric" })}
          </div>
          <div className="flex gap-4 mt-2 text-xs">
            {(user.roles || [user.role]).includes("buyer") && (
              <div className="inline-flex items-center gap-1" data-testid="profile-rating-buyer">
                <StarRating value={Math.round(user.rating_buyer || 5)} readOnly size={12} testid="profile-rating-buyer-stars" />
                <span className="font-mono">{(user.rating_buyer || 5).toFixed(1)}</span>
                <span style={{color: "var(--app-muted)"}}>({user.reviews_count_buyer || 0} sebagai Pembeli)</span>
              </div>
            )}
            {(user.roles || [user.role]).includes("jastiper") && (
              <div className="inline-flex items-center gap-1" data-testid="profile-rating-jastiper">
                <StarRating value={Math.round(user.rating_jastiper || 5)} readOnly size={12} testid="profile-rating-jastiper-stars" />
                <span className="font-mono">{(user.rating_jastiper || 5).toFixed(1)}</span>
                <span style={{color: "var(--app-muted)"}}>({user.reviews_count_jastiper || 0} sebagai Jastiper)</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reviews received */}
      <ProfileReviews userId={user.id} />

      {/* Personal Info */}
      <div className="surface p-6 space-y-4" data-testid="personal-info-section">
        <div className="label-caps flex items-center gap-2">
          <UserIcon size={12} strokeWidth={1.5} /> Informasi Pribadi
        </div>
        <Field label="Nama Lengkap">
          <input className="input-flat" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" />
        </Field>
        <Field label="Nomor Telepon">
          <input className="input-flat" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone-input" />
        </Field>
        <Field label="Lokasi (Kota/Provinsi)">
          <input className="input-flat" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="profile-location-input" />
        </Field>
      </div>

      {/* Privacy Settings (any account with jastiper role — i.e. all unified accounts) */}
      {hasJastiperRole && (
        <div className="surface p-6 space-y-5" data-testid="privacy-section">
          <div>
            <div className="label-caps flex items-center gap-2" style={{color: "var(--app-brand)"}}>
              <Lock size={12} strokeWidth={2} /> Privasi & Anti-Perang Harga
            </div>
            <p className="text-sm mt-2" style={{color: "var(--app-muted)"}}>
              Atur visibilitas katalog dan harga Anda. Jastiper lain tidak bisa melihat harga atau negosiasi private Anda.
            </p>
          </div>

          {/* Default privacy */}
          <div className="space-y-2">
            <label className="text-sm font-medium" data-testid="default-privacy-label">Default Privasi Item Baru</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { value: "public", icon: Eye, title: "Publik", desc: "Harga terlihat semua orang" },
                { value: "hide_price", icon: Lock, title: "Sembunyikan Harga", desc: "Pembeli minta penawaran dulu" },
                { value: "private", icon: ShieldCheck, title: "Private Penuh", desc: "Default hide_price aktif" },
              ].map((opt) => {
                const active = form.default_catalog_privacy === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm({ ...form, default_catalog_privacy: opt.value })}
                    className="border-2 p-3 text-left transition-colors"
                    style={{
                      borderColor: active ? "var(--app-brand)" : "var(--app-border)",
                      backgroundColor: active ? "var(--app-brand-soft)" : "transparent",
                    }}
                    data-testid={`privacy-option-${opt.value}`}
                  >
                    <opt.icon size={16} strokeWidth={1.5} className="mb-2" style={{color: active ? "var(--app-brand)" : "var(--app-muted)"}} />
                    <div className="font-medium text-sm">{opt.title}</div>
                    <div className="text-[11px] mt-0.5" style={{color: "var(--app-muted)"}}>{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Hide from search master toggle */}
          <div className="border-2 p-4 flex items-start gap-3" style={{borderColor: form.hide_prices_from_search ? "var(--app-brand)" : "var(--app-border)", backgroundColor: form.hide_prices_from_search ? "var(--app-brand-soft)" : "transparent"}} data-testid="hide-from-search-section">
            <button
              type="button"
              onClick={() => setForm({ ...form, hide_prices_from_search: !form.hide_prices_from_search })}
              role="switch"
              aria-checked={form.hide_prices_from_search}
              className="relative inline-flex h-6 w-11 flex-shrink-0 mt-0.5 transition-colors"
              style={{backgroundColor: form.hide_prices_from_search ? "var(--app-brand)" : "#d4d4d8"}}
              data-testid="hide-from-search-toggle"
            >
              <span
                className="absolute top-0.5 inline-block h-5 w-5 bg-white shadow transition-transform"
                style={{transform: form.hide_prices_from_search ? "translateX(22px)" : "translateX(2px)"}}
              />
            </button>
            <div className="flex-1">
              <div className="font-medium text-sm flex items-center gap-2">
                {form.hide_prices_from_search ? <EyeOff size={14} strokeWidth={2} /> : <Globe size={14} strokeWidth={2} />}
                Sembunyikan saya dari pencarian publik
              </div>
              <p className="text-xs mt-1" style={{color: "var(--app-muted)"}}>
                Trip Anda tidak akan muncul di landing page atau /trips publik. Pembeli hanya bisa akses via direct link / undangan.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Iter 25 — Bank Info (for refund destination) */}
      <div className="surface p-6 space-y-4" data-testid="bank-info-section">
        <div>
          <div className="label-caps flex items-center gap-2">
            <CreditCard size={12} strokeWidth={1.5} /> Informasi Rekening (Tujuan Refund)
          </div>
          <p className="text-sm mt-2" style={{color: "var(--app-muted)"}}>
            Dana refund akan ditransfer ke rekening ini jika ada trip yang dibatalkan.
            Pastikan nama pemilik sesuai dengan rekening Anda. Opsional — isi saat dibutuhkan.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Nama Bank">
            <input className="input-flat" placeholder="contoh: BCA / Mandiri / OVO" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="profile-bank-name-input" />
          </Field>
          <Field label="Nomor Rekening">
            <input className="input-flat font-mono" placeholder="1234567890" value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} data-testid="profile-bank-number-input" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Nama Pemilik Rekening (sesuai buku tabungan)">
              <input className="input-flat" placeholder="NAMA LENGKAP SESUAI REKENING" value={form.bank_account_holder} onChange={(e) => setForm({ ...form, bank_account_holder: e.target.value.toUpperCase() })} data-testid="profile-bank-holder-input" />
            </Field>
          </div>
        </div>
      </div>

      {/* Iter 25 — Referral Program */}
      <div className="surface p-6 space-y-5" data-testid="referral-section">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="label-caps flex items-center gap-2" style={{color: "var(--app-brand)"}}>
              <Gift size={12} strokeWidth={2} /> Program Referral
            </div>
            <p className="text-sm mt-2" style={{color: "var(--app-muted)"}}>
              Bagikan kode referral Anda. Setiap kali teman yang Anda undang bertransaksi (sebagai
              pembeli atau jastiper), Anda dapat <strong>{referral?.bonus_percent ?? 20}%</strong>{" "}
              dari fee platform.
            </p>
          </div>
          {leaderboardEnabled && (
            <Link
              to="/leaderboard"
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border whitespace-nowrap hover:bg-[var(--app-surface-alt)] transition-colors flex-shrink-0"
              style={{borderColor: "var(--app-border)", color: "var(--app-brand)"}}
              data-testid="profile-leaderboard-link"
            >
              🏆 Top Referrer
            </Link>
          )}
        </div>

        {/* Earnings summary */}
        <div className="grid grid-cols-3 gap-0 border" style={{borderColor: "var(--app-border)"}}>
          <div className="p-4 text-center border-r" style={{borderColor: "var(--app-border)"}} data-testid="referral-stat-balance">
            <div className="label-caps text-[10px]" style={{color: "var(--app-muted)"}}>Saldo</div>
            <div className="font-display text-xl tracking-tight mt-1" style={{color: "var(--app-brand)"}}>
              {formatIDR(referral?.earnings_balance || 0)}
            </div>
          </div>
          <div className="p-4 text-center border-r" style={{borderColor: "var(--app-border)"}} data-testid="referral-stat-total">
            <div className="label-caps text-[10px]" style={{color: "var(--app-muted)"}}>Total Earned</div>
            <div className="font-display text-xl tracking-tight mt-1">
              {formatIDR(referral?.earnings_total || 0)}
            </div>
          </div>
          <div className="p-4 text-center" data-testid="referral-stat-count">
            <div className="label-caps text-[10px]" style={{color: "var(--app-muted)"}}>Teman Diajak</div>
            <div className="font-display text-xl tracking-tight mt-1">
              {referral?.referred_count ?? 0}
            </div>
          </div>
        </div>

        {/* Code setup or display */}
        {!referral?.referral_code ? (
          <div className="space-y-2">
            <label className="text-sm font-medium">Buat Kode Referral Anda</label>
            <p className="text-xs" style={{color: "var(--app-muted)"}}>
              Pilih kode unik (3-32 karakter, huruf kecil/angka/dash). Tidak bisa diubah setelah disimpan.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                className="input-flat font-mono flex-1"
                placeholder="contoh: andi-jastip"
                value={refCodeDraft}
                onChange={(e) => setRefCodeDraft(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                maxLength={32}
                data-testid="profile-ref-code-input"
              />
              <button
                onClick={saveReferralCode}
                disabled={savingRefCode || refCodeDraft.length < 3}
                className="btn-primary-flat text-sm whitespace-nowrap"
                data-testid="profile-ref-code-save-btn"
              >
                {savingRefCode ? "..." : "Simpan"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="label-caps text-[10px] mb-1.5" style={{color: "var(--app-muted)"}}>Kode Anda</div>
              <div className="flex items-center gap-2">
                <div className="input-flat font-mono flex-1 select-all" data-testid="profile-ref-code-display">{referral.referral_code}</div>
                <button
                  onClick={copyRefLink}
                  className="px-3 py-2 text-sm border hover:bg-[var(--app-surface-alt)] transition-colors flex items-center gap-1.5"
                  style={{borderColor: "var(--app-border)"}}
                  data-testid="copy-ref-link-btn"
                >
                  <Copy size={13} strokeWidth={1.5} /> Salin Link
                </button>
              </div>
              <p className="text-[11px] mt-1.5" style={{color: "var(--app-muted)"}}>
                Bagikan link: <span className="font-mono">{window.location.origin}/auth?ref={referral.referral_code}</span>
              </p>
            </div>

            {referral.entries?.length > 0 && (
              <div>
                <div className="label-caps text-[10px] mb-1.5" style={{color: "var(--app-muted)"}}>Riwayat Earning (10 terbaru)</div>
                <div className="border max-h-64 overflow-y-auto" style={{borderColor: "var(--app-border)"}} data-testid="referral-entries-list">
                  {referral.entries.slice(0, 10).map((e) => (
                    <div key={e.id} className="flex items-center justify-between p-2.5 border-b text-xs gap-2" style={{borderColor: "var(--app-border)"}}>
                      <div className="min-w-0">
                        <div className="truncate">{e.referee_name}</div>
                        <div className="font-mono text-[10px]" style={{color: "var(--app-muted)"}}>
                          {e.source === "buyer_fee" ? "Buyer fee" : "Jastiper fee"} · {formatDateTime(e.created_at)}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-mono font-semibold" style={{color: "var(--app-brand)"}}>+{formatIDR(e.amount)}</div>
                        <div className="text-[10px]" style={{color: "var(--app-muted)"}}>{e.bonus_percent}% dari {formatIDR(e.base_fee)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] mt-1.5" style={{color: "var(--app-muted)"}}>
                  Untuk pencairan saldo, hubungi admin via support (fitur withdraw otomatis segera hadir).
                </p>
              </div>
            )}
          </div>
        )}

        {user.referred_by_code && (
          <div className="text-xs p-3 border-l-2" style={{borderColor: "var(--app-brand)", backgroundColor: "var(--app-surface-alt)"}} data-testid="profile-referred-by">
            Anda didaftarkan oleh: <strong className="font-mono">{user.referred_by_code}</strong>
          </div>
        )}
      </div>

      {/* Save */}
      <div className="flex justify-between items-center sticky bottom-4">
        <button
          onClick={logout}
          className="text-sm inline-flex items-center gap-2 px-4 py-2 border hover:bg-red-50 hover:border-red-300 hover:text-red-700 rounded-md"
          style={{borderColor: "var(--app-border)"}}
          data-testid="logout-btn"
        >
          <LogOut size={14} strokeWidth={1.5} /> Logout
        </button>
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="btn-primary-flat disabled:opacity-50"
          data-testid="save-profile-btn"
        >
          {saving ? "Menyimpan..." : "Simpan Perubahan"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-caps mb-2 block">{label}</label>
      {children}
    </div>
  );
}


function ProfileReviews({ userId }) {
  const [data, setData] = useState({ reviews: [] });
  const [tab, setTab] = useState("jastiper");
  useEffect(() => {
    api.get(`/users/${userId}/reviews?role=${tab}&limit=20`).then((r) => setData(r.data)).catch(() => {});
  }, [userId, tab]);
  return (
    <div className="surface p-6 space-y-4" data-testid="profile-reviews">
      <div className="flex items-center justify-between">
        <div className="label-caps flex items-center gap-2">
          <Star size={12} strokeWidth={2} style={{color: "#f59e0b"}} /> Ulasan yang Diterima
        </div>
        <div className="inline-flex border rounded-md overflow-hidden" style={{borderColor: "var(--app-border)"}}>
          <button onClick={() => setTab("jastiper")} className="px-3 py-1 text-xs" style={{backgroundColor: tab === "jastiper" ? "var(--app-brand)" : "transparent", color: tab === "jastiper" ? "white" : "var(--app-fg)"}} data-testid="reviews-tab-jastiper">Sebagai Jastiper</button>
          <button onClick={() => setTab("buyer")} className="px-3 py-1 text-xs" style={{backgroundColor: tab === "buyer" ? "var(--app-brand)" : "transparent", color: tab === "buyer" ? "white" : "var(--app-fg)"}} data-testid="reviews-tab-buyer">Sebagai Pembeli</button>
        </div>
      </div>
      {data.reviews.length === 0 ? (
        <p className="text-sm text-center py-6" style={{color: "var(--app-muted)"}}>Belum ada ulasan.</p>
      ) : (
        <div className="space-y-3">
          {data.reviews.map((r) => (
            <div key={r.id} className="border-l-2 pl-3 py-1" style={{borderColor: "var(--app-border)"}} data-testid={`review-item-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <StarRating value={r.rating} readOnly size={12} testid={`review-stars-${r.id}`} />
                  <span className="text-xs font-medium">{r.from_user_name}</span>
                </div>
                <span className="text-xs font-mono" style={{color: "var(--app-muted)"}}>{formatDateTime(r.created_at)}</span>
              </div>
              {r.comment && <p className="text-sm mt-1 italic">"{r.comment}"</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
